import React, { useEffect, useState, useCallback } from "react";

import { infectionStateColors } from "./Boid";

export default function GraphCanvas({
  canvasWidth,
  canvasHeight,
  boids,
  id,
  addResetListener,
  addSimHistory,
  notifySimDone
}) {
  const [ctx, setCtx] = useState();
  const [boidGraph, setBoidGraph] = useState([]); // {numInfected:0, numNormal:0}
  const [lastGraphUpdate, setLastGraphUpdate] = useState(Date.now());
  const [isGraphDone, setIsGraphDone] = useState(false);
  // latches true once the run has completed so completion is detected only
  // once. Without this the "done" condition stays true and re-fires on every
  // boid tick, flooding the app with re-renders (canvas flicker) and history.
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    // notify that graph is done
    if (isGraphDone) {
      const histGraph = boidGraph.map(bg => {
        return bg.numInfected / bg.numNormal;
      });
      addSimHistory(histGraph);
      setIsGraphDone(false);
    }
  }, [isGraphDone, boidGraph, addSimHistory]);

  useEffect(() => {
    if (isGraphDone && boidGraph && boidGraph.length > 0) {
      /*{numDead: 0,
        numImmune: 0,
        numInfected: 1,
        numNormal: 99,
        numTotal: 100}
        */
      const boid_data = boidGraph.reduce((acc, curr) => {
        return {
          ...acc,
          immune_array: [...acc.immune_array, curr.numImmune],
          dead_array: [...acc.dead_array, curr.numDead],
          healthy_array: [...acc.healthy_array, curr.numNormal],
          infected_array: [...acc.infected_array, curr.numInfected],
        }
      }, {
        population: boidGraph[0].numTotal,
        immune_array: [],
        dead_array: [],
        healthy_array: [],
        infected_array: []
      })
      notifySimDone(boid_data);
    }
  }, [isGraphDone, notifySimDone]);

  useEffect(() => {
    addResetListener(() => {
      setBoidGraph([]);
      setIsGraphDone(false);
      setIsDone(false);
    });
  }, [addResetListener]);

  useEffect(() => {
    // drawGraph
    const lineWidth = 1;
    if (ctx) {
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      boidGraph.forEach((bg, i) => {
        const infectedHeight = (bg.numInfected / bg.numTotal) * canvasHeight;
        const immuneHeight = (bg.numImmune / bg.numTotal) * canvasHeight;
        const normalHeight = (bg.numNormal / bg.numTotal) * canvasHeight;
        const deadHeight = (bg.numDead / bg.numTotal) * canvasHeight;

        // normal
        ctx.beginPath();
        ctx.fillStyle = infectionStateColors["normal"];
        ctx.fillRect(i * lineWidth, 0, lineWidth, normalHeight);
        ctx.closePath();

        // infected
        ctx.beginPath();
        ctx.fillStyle = infectionStateColors["infected"];
        ctx.fillRect(i * lineWidth, normalHeight, lineWidth, infectedHeight);
        ctx.closePath();

        // immune
        ctx.beginPath();
        ctx.fillStyle = infectionStateColors["immune"];
        ctx.fillRect(
          i * lineWidth,
          normalHeight + infectedHeight,
          lineWidth,
          immuneHeight
        );
        ctx.closePath();

        // dead
        ctx.beginPath();
        ctx.fillStyle = infectionStateColors["dead"];
        ctx.fillRect(
          i * lineWidth,
          normalHeight + infectedHeight + immuneHeight,
          lineWidth,
          deadHeight
        );
        ctx.closePath();
      });
    }
  }, [ctx, boidGraph, canvasHeight, canvasWidth]);

  const shouldUpdateBoidGraph = useCallback(() => {
    const graphUpdateInterval = 60;
    const numInfected = boids.filter(b => b.state === "infected").length;
    return lastGraphUpdate + graphUpdateInterval <= Date.now();
  }, [lastGraphUpdate, boids]);

  useEffect(() => {
    setCtx(document.getElementById(id).getContext("2d"));
  }, [id]);

  useEffect(() => {
    setLastGraphUpdate(Date.now());
  }, [boidGraph]);

  useEffect(() => {
    // updateBoidGraph
    if (isDone) {
      // run already completed; stop detecting/redrawing until reset so we
      // don't re-fire "done" on every boid tick.
      return;
    }
    const numInfected = boids.filter(b => b.state === "infected").length;
    const numImmune = boids.filter(b => b.state === "immune").length;
    if (shouldUpdateBoidGraph()) {
      if (numInfected === 0 && numImmune > 0) {
        // no infected left -> the run is over
        setIsDone(true);
        setIsGraphDone(true);
      } else {
        // append the new sample, keeping only the last canvasWidth of them so
        // the graph scrolls: once full, old samples fall off the left edge and
        // new ones enter from the right
        setBoidGraph(boidGraph => {
          const next = [
            ...boidGraph,
            {
              numTotal: boids.length,
              numNormal: boids.filter(b => b.state === "normal").length,
              numInfected: boids.filter(b => b.state === "infected").length,
              numImmune: boids.filter(b => b.state === "immune").length,
              numDead: boids.filter(b => b.state === "dead").length
            }
          ];
          return next.length > canvasWidth
            ? next.slice(next.length - canvasWidth)
            : next;
        });
      }
    }
  }, [boids, shouldUpdateBoidGraph, canvasWidth, boidGraph.length, isDone]);

  return (
    <div className="graphContainer">
      <canvas id={id} width={canvasWidth} height={canvasHeight} />
      {/*<div>
        <button onClick={handleStepBoidGraph}>step</button>
        <button
          onClick={() => {
            setBoidGraph([]);
          }}
        >
          reset
        </button>
        </div>*/}
    </div>
  );
}
